// Typed wrappers around the recspec api. All requests send credentials so
// the session cookie rides along.

export type AuthMethod = "bearer" | "bearer_api_key" | "cookie";

export type MeResponse =
  | { auth_method: "bearer"; identity: { email: string } }
  | { auth_method: "bearer_api_key"; identity: { uid: string } }
  | { auth_method: "cookie"; identity: { gh_login: string; email?: string } };

// --- User-issued API keys ---

export interface ApiKeySummary {
  key_id: string;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at?: string;
  revoked_at?: string;
}

export interface CreateApiKeyResponse {
  /** Plaintext key, returned ONLY on creation. Never persisted client-side. */
  key: string;
  record: ApiKeySummary;
}

export interface ListApiKeysResponse {
  keys: ApiKeySummary[];
}

// ── Organizations (tenancy axis) ─────────────────────────────────

export type OrgRole = "owner" | "admin" | "member";
export type OrgStatus = "pending" | "active" | "archived";

export interface EnderecoFiscal {
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  municipio: string;
  /** 2-char UF code, e.g. "SP". */
  uf: string;
  /** CEP, with or without hyphen (e.g. "01310-100"). */
  cep: string;
}

export interface OrgPublic {
  org_id: string;
  name: string;
  slug: string;
  owner_user_id: string;
  status: OrgStatus;
  cnpj?: string;
  razao_social?: string;
  endereco_fiscal?: EnderecoFiscal;
  codigo_servico_iss?: string;
  inscricao_municipal?: string;
  created_at: string;
  updated_at: string;
}

export interface OrgWithRole extends OrgPublic {
  role: OrgRole;
}

export interface OrgListItem {
  org_id: string;
  name: string;
  slug: string;
  role: OrgRole;
}

export interface OrgsListResponse {
  orgs: OrgListItem[];
}

export interface CreateOrgRequest {
  name: string;
  slug?: string;
  cnpj?: string;
}

export interface UpdateOrgRequest {
  name?: string;
  slug?: string;
  cnpj?: string;
  razao_social?: string;
  endereco_fiscal?: EnderecoFiscal;
  codigo_servico_iss?: string;
  inscricao_municipal?: string;
}

export interface MemberSummary {
  user_id: string;
  role: OrgRole;
  joined_at: string;
}

export interface MembersListResponse {
  members: MemberSummary[];
}

export interface AddMemberRequest {
  user_id: string;
  role: OrgRole;
}

export interface CreateUploadUrlRequest {
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface CreateUploadUrlResponse {
  upload_url: string;
  gcs_uri: string;
  expires_at: string;
  required_headers: Record<string, string>;
}

export interface SubmitJobRequest {
  gcs_uri: string;
  idempotency_key: string;
  mime?: string;
}

export interface SubmitJobResponse {
  job_id: string;
  status: string;
}

export interface StageProgress {
  stage: string;
  pct: number;
  heartbeat_at: string;
}

export interface JobView {
  job_id: string;
  status: string;
  stage_progress: StageProgress | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  cost_usd_estimate: number;
  cost_display: string;
  duration_display: string | null;
  size_display: string | null;
  health: string;
  video_retention_until: string | null;
  error: { code: string; message: string; retriable: boolean } | null;
}

export interface JobsListResponse {
  jobs: JobView[];
  next_cursor: string | null;
}

// --- Artifact endpoint response shapes ---

export interface TranscriptView {
  body: unknown;
}
export interface TimelineView {
  body: unknown;
}
export interface AnnotationsView {
  body: unknown;
}

export interface FrameEntry {
  index: number;
  signed_url: string;
  captured_at_seconds: number | null;
  annotation: unknown | null;
}
export interface FramesView {
  frames: FrameEntry[];
}

export interface SpecBundleView {
  proposal: string | null;
  design: string | null;
  tasks: string | null;
  specs: Record<string, string>;
}

export interface VideoArtifactView {
  signed_url: string;
  expires_at: string;
  content_type: string;
  size_bytes: number | null;
}

// --- Meeting endpoints ---

export interface CreateMeetingRequest {
  meet_url: string;
  idempotency_key: string;
  /// Pinned expert references (e.g. ["software-spec-author@1"]). The
  /// SPA's meeting-create form picks exactly one (cap-meeting-to-single-agent).
  expert_ids?: string[];
  /// Optional per-expert tone selection (keyed by bare expert id).
  /// **Legacy field** — new code should set `agent` instead. Kept for
  /// the transition while the SPA Configure rebuild lands.
  expert_tones?: Record<string, "consultant" | "advisor" | "reviewer">;
  /// Live-meeting channel toggles. Optional; backend default = qa on,
  /// frames on, voice highlights.
  channels?: MeetingChannels;
  /// How the meeting was initiated. Optional; defaults to "paste".
  trigger_kind?: "paste" | "start";
  /// Meeting language. Optional; defaults to "EN".
  language?: Language;
  /// Per-meeting agent configuration introduced by
  /// extract-experts-to-yaml-with-composed-archetypes-and-axes. When
  /// supplied, the api persists it on `MeetingDoc.agent` and ignores
  /// `expert_tones` for the matching expert.
  agent?: AgentConfigBody;
  /// IETF BCP 47 locale hint (`en-GB`, `pt-BR`, etc.) used by the api's
  /// register-inference at create time.
  locale?: string;
}

export type Assertiveness = "suggests" | "recommends" | "directs";
export type Responsiveness = "brisk" | "balanced" | "warm";
export type Evidence = "confident" | "cited" | "rigorous";
export type Proactiveness = "notes-only" | "hold";
export type Channel = "chat" | "voice";

export interface AxisValues {
  assertiveness: Assertiveness;
  responsiveness: Responsiveness;
  evidence: Evidence;
}

export interface AgentConfigBody {
  expert_id: string;
  archetype_id: string;
  axes: AxisValues;
  proactiveness: Proactiveness;
  channel: Channel;
  frames: boolean;
}

export interface ArchetypeSummary {
  id: string;
  display_name: string;
  one_line: string;
  baseline: AxisValues;
  is_default: boolean;
}

export type VoiceMode = "off" | "highlights" | "on";

export interface MeetingChannels {
  qa: boolean;
  frames: boolean;
  voice: VoiceMode;
}

export interface ExpertSummary {
  id: string;
  version: number;
  display_name: string;
  short_name: string;
  persona_summary: string;
  default_priority: number;
  /** 2-letter monogram (e.g. "SP", "AS"). */
  monogram: string;
  /** Kebab-case palette token. SPA accesses color via `var(--<color_token>)`. */
  color_token: string;
  /** Stable voice identifier (e.g. "warm-baritone"). Hash seed for VoiceSignature. */
  voice_id: string;
  /** Human-readable voice description (e.g. "Crisp tenor · slight Pacific NW"). */
  voice_desc: string;
  /** Languages this expert can render artifacts in. */
  supported_languages: Language[];
  finalize_artifacts: ArtifactSpec[];
  /** Per-expert archetype options (empty when the api booted without
   *  EXPERTS_DIR). Used by the Configure step's Archetype band. */
  archetypes?: ArchetypeSummary[];
}

export type Language = "EN" | "PT" | "DE";

export type QaVote = "pending" | "up" | "down";
// `awaiting-cue` was retired by `introduce-approval-queue-concept` —
// legacy items in that state deserialize as `pending` server-side, so
// the SPA never sees it on fresh fetches.
export type QaStatus = "pending" | "posted" | "spoken" | "dismissed";

export interface QaItem {
  id: string;
  expert_id: string;
  text: string;
  rationale: string;
  vote: QaVote;
  status: QaStatus;
}

export interface VoteQaItemResponse {
  vote: QaVote;
  status: QaStatus;
}

export interface ArtifactSpec {
  filename: string;
  format:
    | "markdown"
    | "csv"
    | "json"
    | "jsonl"
    | "mermaid"
    | "png"
    | "plaintext";
  description: string;
}

export interface CreateMeetingResponse {
  meeting_id: string;
  share_token: string;
  state: string;
}

export interface LiveQuestion {
  id: string;
  text: string;
  category: string;
  created_at: string;
  asked: boolean;
  asked_at: string | null;
  answer_text: string | null;
  answered_at: string | null;
  /** "owner" = user typed; "transcript" = Flash auto-resolved from later
   * transcript window. null on unanswered or pre-feature docs. */
  answer_source: "owner" | "transcript" | null;
}

export interface MeetingBotError {
  /** Top-level Recall status, typically `"fatal"`. */
  code: string;
  /** Recall sub-code from `status_changes[].sub_code`. The SPA keys
   *  on this to render context-specific guidance — e.g.
   *  `meeting_requires_sign_in` →
   *  "Your Google Meet requires sign-in. Disable the restriction
   *  in Meet's Host management settings (or wait for signed-in bots
   *  to be configured)." */
  sub_code?: string;
  /** Recall's human-readable error message (often includes a
   *  resolution hint). Always present. */
  message: string;
  /** When the worker's watchdog observed the fatal state. */
  detected_at: string;
}

export interface MeetingStateResponse {
  meeting_id: string;
  owner_login: string;
  state: "in_meeting" | "meeting_over" | "finalized";
  created_at: string;
  joined_at: string | null;
  entered_meeting_over_at: string | null;
  finalized_at: string | null;
  last_refresh_at: string | null;
  refresh_count: number;
  recall_bot_id: string | null;
  /** Terminal-fatal error from Recall.ai bot lifecycle. `null` for
   *  healthy meetings; populated by the worker's watchdog within
   *  ~30 s of dispatch when the bot transitions to `fatal` (Meet
   *  rejected the anonymous bot, host disabled knocking, etc.).
   *  The meeting auto-transitions to `meeting_over` when this is
   *  set, so the SPA can render an actionable banner instead of
   *  a hung "joining…" state. */
  bot_error?: MeetingBotError;
  draft: unknown;
  uncertainties: unknown;
  /** Questions extracted continuously during the meeting (live-questions-to-chat). */
  live_questions: LiveQuestion[];
  cost_usd: number;
  recall_meeting_minutes_usd: number;
  recall_transcript_minutes_usd: number;
  vertex_author_usd: number;
  resolved_job_id: string | null;
  /** Pinned expert references picked at meeting create. Always at least one entry. */
  expert_ids: string[];
  /** Per-expert tone overrides. */
  expert_tones: Record<string, "consultant" | "advisor" | "reviewer">;
  /** Per-expert finalize stage written by the worker as it iterates picked
   *  experts. Empty before finalize starts; populated as each expert
   *  transitions through Pending → Running → Done (or Failed). The SPA's
   *  FinalizingView reads this to render real progress instead of
   *  time-based skeletons. */
  expert_progress?: Record<string, "pending" | "running" | "done" | "failed">;
  channels: MeetingChannels;
  trigger_kind: "paste" | "start";
  language: Language;
  qa_items: QaItem[];
  /** Resolved agent config — explicit value when set, otherwise
   *  backfilled by the api from the legacy `expert_tones` + frames.
   *  Always present; surfaces archetype identity to Desk / Tweaks /
   *  Home tooltip surfaces. */
  agent: AgentConfigBody;
  /** Per-expert continuously-updatable live state (keyed by bare
   *  expert id). The `value` is opaque per-expert JSON the SPA renders
   *  with expert-specific components. Omitted / empty when no expert
   *  has written to its slot yet. See `add-expert-reducer-abstraction`. */
  live_state?: Record<string, ExpertLiveState>;
}

/** One slot on `MeetingStateResponse.live_state`. `value` is opaque —
 *  each expert's live panel knows how to render its own shape. */
export interface ExpertLiveState {
  value: unknown;
  version: number;
  updated_at: string;
  reducer_pass_count: number;
}

/** Decision status mirror of `openspec_video_core::notes::DecisionStatus`. */
export type DecisionStatus =
  | "aligned"
  | "needs_further_discussion"
  | "disagreed"
  | "shelved";

/** Confidence label mirror of `openspec_video_core::notes::Confidence`. */
export type Confidence = "high" | "medium" | "low";

/** Transcript anchor required on every Decision / ActionItem / KeyQuote. */
export interface TranscriptAnchor {
  segment_id: string;
  start_seconds: number;
}

export interface UserOverrides {
  owner?: string;
  due_date?: string;
  description?: string;
}

export interface Decision {
  id: string;
  statement: string;
  status: DecisionStatus;
  owners: string[];
  rationale: string;
  transcript_anchor: TranscriptAnchor;
  confidence: Confidence;
}

export interface ActionItem {
  id: string;
  description: string;
  owner?: string;
  due_date?: string;
  source_quote: string;
  confidence: Confidence;
  transcript_anchor: TranscriptAnchor;
  user_confirmed: boolean;
  user_overrides?: UserOverrides;
}

export interface OpenQuestion {
  id: string;
  text: string;
  suggested_owner?: string;
  category: string;
  transcript_anchor: TranscriptAnchor;
}

export interface NotesTopic {
  id: string;
  title: string;
  time_start_seconds: number;
  time_end_seconds: number;
  bullets: string[];
  key_quotes_ids?: string[];
}

export interface KeyQuote {
  id: string;
  text: string;
  speaker: string;
  transcript_anchor: TranscriptAnchor;
}

export type FollowUpKind = "meeting" | "doc" | "prep" | "other";

export interface FollowUp {
  id: string;
  kind: FollowUpKind;
  description: string;
  suggested_owner?: string;
}

/** Notetaker's typed live state — what gets rendered in the SPA Notes
 *  panel. Decoded from `MeetingStateResponse.live_state.notetaker.value`. */
export interface NotesArtifact {
  headline: string;
  decisions: Decision[];
  action_items: ActionItem[];
  open_questions: OpenQuestion[];
  topics: NotesTopic[];
  key_quotes: KeyQuote[];
  follow_ups: FollowUp[];
  retrieval_prompts?: string[];
}

/** Body for `PATCH /v1/meetings/{id}/notes/action-items/{item_id}`. */
export interface PatchActionItemRequest {
  owner?: string | null;
  due_date?: string | null;
  description?: string | null;
  /** Defaults to true on the server when omitted. */
  confirmed?: boolean;
}

export interface PatchActionItemResponse {
  action_item: ActionItem;
}

export interface PostQuestionsResponse {
  posted: number;
}

export interface MeetingListEntry {
  /** Archetype id selected for this meeting. Drives the row's
   *  archetype tooltip. Empty when the expert has no archetypes
   *  declared (legacy state). */
  archetype_id?: string;
  meeting_id: string;
  state: "in_meeting" | "meeting_over" | "finalized";
  created_at: string;
  joined_at: string | null;
  finalized_at: string | null;
  meet_url: string;
  resolved_job_id: string | null;
  live_questions_count: number;
  expires_at: string | null;
  /** Bare expert ids (no @version) for the meeting's picked experts.
   *  Drives the row's ExpertMark monogram stack. */
  expert_ids: string[];
  /** Aggregate counts derived once at finalize end. Zero for legacy
   *  or non-finalized meetings. Drives the row's counts strip. */
  decisions_count: number;
  risks_count: number;
  frames_count: number;
}

export interface MeetingsListResponse {
  meetings: MeetingListEntry[];
}

/** One entry in a finalized job's bundle zip. Source of truth for the
 *  Finalized view's bundle tree — the SPA no longer speculates about
 *  what's in the zip from the expert registry. */
export interface BundleFile {
  /** Zip-entry path, e.g. `MEETING.md` or `software-spec-author/proposal.md`. */
  path: string;
  /** Uncompressed byte count from the central-directory entry. */
  size: number;
  /** MIME type guess from extension, mirrored from the api side. */
  content_type: string;
}

export interface BundleFilesResponse {
  files: BundleFile[];
}

/** Captured frame from a meeting. Mirrors the api response of
 *  `GET /v1/meetings/{id}/frames`. Each entry carries a 1-hour
 *  signed_url that the SPA can drop directly into an `<img>` tag. */
export interface FrameView {
  id: string;
  /** Wall-clock offset within the meeting, formatted "HH:MM:SS". */
  t: string;
  kind: string;
  label: string;
  summary: string;
  capturedBy: "auto" | "manual";
  relevantTo: string[];
  notes: { expert_id: string; text: string }[];
  referencedIn: string[];
  /** 1-hour V4 signed URL for the frame's JPG. Empty when signing
   *  failed — caller renders a placeholder. */
  signed_url: string;
}

export interface FinalizeMeetingResponse {
  meeting_id: string;
  job_id: string;
}

export interface TranscriptSegment {
  start_seconds: number;
  end_seconds: number;
  speaker: string;
  text: string;
  expert_id?: string;
}

export interface TranscriptResponse {
  segments: TranscriptSegment[];
}

export interface LiveStatusResponse {
  // Recall lifecycle: dispatching | in_call | leaving | done | failed.
  // null when no bot has been dispatched yet, or Recall is unreachable.
  bot_status: string | null;
  bot_status_detail: string | null;
  // Cumulative transcribed words from Recall's `recallai_streaming`
  // provider. The "your mic is reaching the bot" signal — 0 means no
  // speech has been transcribed yet.
  transcript_words_received: number;
  // ISO-8601; null when no transcript event has arrived yet.
  last_transcript_at: string | null;
  // in_meeting | meeting_over | finalized.
  meeting_state: string;
}

export interface RotateShareTokenResponse {
  meeting_id: string;
  share_token: string;
}

export interface AdminPreviewResponse {
  gh_login: string;
  email?: string;
  name?: string;
  status: "pending" | "approved" | "rejected";
  requested_at: string;
}

export interface AdminApproveResponse {
  now: "approved" | "rejected";
}

export interface WaitlistMeResponse {
  gh_login: string;
  has_usable_email: boolean;
}

/** Subscribers fire on every typed api error _before_ the error is
 *  thrown to the caller. Used by App.tsx to install a global navigation
 *  hook that routes specific error codes (e.g. `meeting_bot_unavailable`)
 *  to the corresponding `/edge/<kind>` screen. The throw still happens
 *  so the caller can react too — the subscriber only gets to *also*
 *  observe the error. */
type ApiErrorListener = (err: ApiError) => void;
const apiErrorListeners = new Set<ApiErrorListener>();

export function onApiError(listener: ApiErrorListener): () => void {
  apiErrorListeners.add(listener);
  return () => apiErrorListeners.delete(listener);
}

function notifyApiError(err: ApiError): void {
  for (const l of apiErrorListeners) {
    try {
      l(err);
    } catch {
      // Listener errors must not block the throw or other listeners.
    }
  }
}

async function jfetch<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });
  if (!resp.ok) {
    let detail = `${resp.status} ${resp.statusText}`;
    let body: ProblemBody | null = null;
    try {
      body = (await resp.json()) as ProblemBody;
      if (body.detail) detail = body.detail;
      else if (body.title) detail = body.title;
    } catch {
      // body wasn't JSON; keep the status text
    }
    // 410 Gone with code=artifact_expired or code=artifact_unavailable is
    // a first-class state — surface it as a typed error so each tab can
    // render the expired placeholder without re-parsing problem bodies.
    // Same pattern for code=meeting_expired (the meeting doc itself was
    // TTL-deleted after the 7-day retention window).
    if (resp.status === 410 && body) {
      const code = problemCode(body);
      if (code === "artifact_expired" || code === "artifact_unavailable") {
        const expiredAt =
          typeof body.expired_at === "string" ? body.expired_at : null;
        throw new ArtifactExpiredError(code, detail, expiredAt);
      }
      if (code === "meeting_expired") {
        const expiredAt =
          typeof body.expired_at === "string" ? body.expired_at : null;
        throw new MeetingExpiredError(detail, expiredAt);
      }
    }
    const code = body ? problemCode(body) : null;
    const err = new ApiError(resp.status, detail, code);
    notifyApiError(err);
    throw err;
  }
  if (resp.status === 204) return undefined as unknown as T;
  return (await resp.json()) as T;
}

interface ProblemBody {
  type?: string;
  title?: string;
  detail?: string;
  expired_at?: string;
}

function problemCode(body: ProblemBody): string | null {
  // The api uses `urn:openspec-video:errors:<code>` for `type`. Strip the
  // namespace so callers can match on the bare code.
  const t = body.type;
  if (!t) return null;
  const i = t.lastIndexOf(":");
  return i >= 0 ? t.slice(i + 1) : t;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Bare error code (e.g. "meeting_bot_unavailable") parsed from the
     *  problem body's `type` field, or null when absent / unparseable. */
    public code: string | null = null,
  ) {
    super(message);
  }
}

export class ArtifactExpiredError extends Error {
  constructor(
    public code: "artifact_expired" | "artifact_unavailable",
    message: string,
    public expiredAt: string | null,
  ) {
    super(message);
  }
}

/// Thrown when the api returns `410 Gone code=meeting_expired` — the
/// meeting doc itself was TTL-deleted after the 7-day retention window.
/// Distinct from `ArtifactExpiredError` because the entire meeting (not
/// just one artifact) is gone; the SPA renders a different placeholder.
export class MeetingExpiredError extends Error {
  constructor(
    message: string,
    public expiredAt: string | null,
  ) {
    super(message);
  }
}

export const api = {
  me: () => jfetch<MeResponse>("/v1/auth/me"),
  logout: () => jfetch<void>("/auth/logout", { method: "POST" }),
  createUploadUrl: (req: CreateUploadUrlRequest) =>
    jfetch<CreateUploadUrlResponse>("/v1/uploads:create", {
      method: "POST",
      body: JSON.stringify(req),
    }),
  submitJob: (req: SubmitJobRequest) =>
    jfetch<SubmitJobResponse>("/v1/jobs", {
      method: "POST",
      body: JSON.stringify(req),
    }),
  getJob: (id: string) => jfetch<JobView>(`/v1/jobs/${encodeURIComponent(id)}`),
  listJobs: (params?: { cursor?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.cursor) q.set("cursor", params.cursor);
    const suffix = q.toString();
    return jfetch<JobsListResponse>(`/v1/jobs${suffix ? `?${suffix}` : ""}`);
  },
  downloadUrl: (id: string) => `/v1/jobs/${encodeURIComponent(id)}/download`,
  evidenceUrl: (id: string) =>
    `/v1/jobs/${encodeURIComponent(id)}/evidence/download`,

  // --- Expert registry ---
  listExperts: (lang?: Language) =>
    jfetch<ExpertSummary[]>(
      lang ? `/v1/experts?lang=${encodeURIComponent(lang)}` : "/v1/experts",
    ),

  /// Read one file out of a finalized job's bundle zip. Used by the
  /// Finalized view to render per-kind previews (md/csv/mermaid/diff)
  /// without downloading the whole zip. Returns the raw text content;
  /// caller is responsible for parsing (e.g. csv → table).
  getJobArtifact: async (jobId: string, path: string): Promise<string> => {
    const resp = await fetch(
      `/v1/jobs/${encodeURIComponent(jobId)}/artifact?path=${encodeURIComponent(path)}`,
      { credentials: "include" },
    );
    if (!resp.ok) {
      throw new ApiError(resp.status, `getJobArtifact: HTTP ${resp.status}`);
    }
    return resp.text();
  },

  /** List the actual entries in a job's bundle zip. Drives the Finalized
   *  view's dynamic bundle tree. Throws `ApiError` with status 404 when
   *  the job doesn't exist / isn't owned by the caller, 409 when the job
   *  isn't terminal (e.g. still running). */
  listJobFiles: (jobId: string) =>
    jfetch<BundleFilesResponse>(
      `/v1/jobs/${encodeURIComponent(jobId)}/files`,
    ).then((r) => r.files),

  /** Fetch the captured-frames manifest for a meeting. Always returns
   *  an array — empty when the meeting hasn't finalized, has frames
   *  disabled, or never captured anything. Each entry includes a
   *  1-hour signed_url. */
  listMeetingFrames: (meetingId: string) =>
    jfetch<{ frames: FrameView[] }>(
      `/v1/meetings/${encodeURIComponent(meetingId)}/frames`,
    ).then((r) => r.frames),

  // --- Meeting endpoints ---
  createMeeting: (req: CreateMeetingRequest) =>
    jfetch<CreateMeetingResponse>("/v1/meetings", {
      method: "POST",
      body: JSON.stringify(req),
    }),
  listMeetings: () => jfetch<MeetingsListResponse>("/v1/meetings"),
  getMeeting: (id: string) =>
    jfetch<MeetingStateResponse>(`/v1/meetings/${encodeURIComponent(id)}`),
  getMeetingLiveStatus: (id: string) =>
    jfetch<LiveStatusResponse>(
      `/v1/meetings/${encodeURIComponent(id)}/live-status`,
    ),
  refreshMeeting: (id: string) =>
    jfetch<{ meeting_id: string }>(
      `/v1/meetings/${encodeURIComponent(id)}/refresh`,
      { method: "POST" },
    ),
  pauseMeeting: (id: string) =>
    jfetch<void>(`/v1/meetings/${encodeURIComponent(id)}/pause`, {
      method: "POST",
    }),
  resumeMeeting: (id: string) =>
    jfetch<void>(`/v1/meetings/${encodeURIComponent(id)}/resume`, {
      method: "POST",
    }),
  answerUncertainty: (
    id: string,
    body: { uncertainty_id: string; answer_text: string },
  ) =>
    jfetch<{ meeting_id: string }>(
      `/v1/meetings/${encodeURIComponent(id)}/answers`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  /**
   * Patch a Notetaker action item with user-confirmed overrides. The
   * server preserves the body fields verbatim across subsequent
   * reducer passes (sets `user_confirmed = true` + `user_overrides`).
   * Throws on 409 — the caller should re-fetch the meeting state and
   * retry against the new version.
   */
  patchActionItem: (
    meetingId: string,
    itemId: string,
    body: PatchActionItemRequest,
  ) =>
    jfetch<PatchActionItemResponse>(
      `/v1/meetings/${encodeURIComponent(meetingId)}/notes/action-items/${encodeURIComponent(itemId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  /**
   * Post live questions to the meeting's chat panel.
   * `question_ids` empty/missing → bulk: post every pending question.
   * Non-empty → post the listed ids verbatim, regardless of `asked` state.
   */
  postQuestions: (id: string, body?: { question_ids?: string[] }) =>
    jfetch<PostQuestionsResponse>(
      `/v1/meetings/${encodeURIComponent(id)}/post-questions`,
      { method: "POST", body: JSON.stringify(body ?? {}) },
    ),
  stopMeeting: (id: string) =>
    jfetch<void>(`/v1/meetings/${encodeURIComponent(id)}/stop`, {
      method: "POST",
    }),
  finalizeMeeting: (id: string) =>
    jfetch<FinalizeMeetingResponse>(
      `/v1/meetings/${encodeURIComponent(id)}/finalize`,
      { method: "POST" },
    ),
  /** Fetch the meeting transcript as JSON. Reachable in any meeting state. */
  getTranscript: (id: string) =>
    jfetch<TranscriptResponse>(
      `/v1/meetings/${encodeURIComponent(id)}/transcript`,
    ),
  voteQaItem: (
    meetingId: string,
    qaId: string,
    vote: "up" | "down" | "pending",
  ) =>
    jfetch<VoteQaItemResponse>(
      `/v1/meetings/${encodeURIComponent(meetingId)}/qa-items/${encodeURIComponent(qaId)}/vote`,
      { method: "POST", body: JSON.stringify({ vote }) },
    ),
  updateChannels: (id: string, body: Partial<MeetingChannels>) =>
    jfetch<{ channels: MeetingChannels }>(
      `/v1/meetings/${encodeURIComponent(id)}/channels`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  updateExpertTones: (
    id: string,
    tones: Record<string, "consultant" | "advisor" | "reviewer">,
  ) =>
    jfetch<{ expert_tones: Record<string, "consultant" | "advisor" | "reviewer"> }>(
      `/v1/meetings/${encodeURIComponent(id)}/expert-tones`,
      { method: "PATCH", body: JSON.stringify({ expert_tones: tones }) },
    ),
  rotateShareToken: (id: string) =>
    jfetch<RotateShareTokenResponse>(
      `/v1/meetings/${encodeURIComponent(id)}/share-token/rotate`,
      { method: "POST" },
    ),

  // --- Waitlist self-serve (collect-missing-email-on-waitlist) ---
  // Identifies the caller via the `recspec_pending` HMAC cookie set
  // on the OAuth waitlist redirect; not the same as the standard
  // session cookie. Returns 401 when called outside that window.
  waitlistMe: () => jfetch<WaitlistMeResponse>("/v1/waitlist/me"),
  setWaitlistEmail: (email: string) =>
    jfetch<void>("/v1/waitlist/email", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  // --- Admin approval flow ---
  // Read-only preview: idempotent, safe to be hit by an email-client
  // preview prefetcher. Returns the pending waitlist entry.
  adminPreview: (token: string) =>
    jfetch<AdminPreviewResponse>(
      `/v1/admin/waitlist/preview?t=${encodeURIComponent(token)}`,
    ),
  // The single mutation. Email scanners follow GETs not POSTs, so this
  // endpoint can never be triggered by a preview prefetch.
  adminApprove: (token: string, action: "approve" | "reject") =>
    jfetch<AdminApproveResponse>("/v1/admin/approve", {
      method: "POST",
      body: JSON.stringify({ t: token, action }),
    }),

  // Artifact inspector endpoints. Each may throw `ArtifactExpiredError`
  // when the underlying GCS object has been lifecycle-deleted (or the
  // job failed before producing the artifact).
  artifactTranscript: (id: string) =>
    jfetch<TranscriptView>(`/v1/jobs/${encodeURIComponent(id)}/artifacts/transcript`),
  artifactTimeline: (id: string) =>
    jfetch<TimelineView>(`/v1/jobs/${encodeURIComponent(id)}/artifacts/timeline`),
  artifactAnnotations: (id: string) =>
    jfetch<AnnotationsView>(`/v1/jobs/${encodeURIComponent(id)}/artifacts/annotations`),
  artifactFrames: (id: string) =>
    jfetch<FramesView>(`/v1/jobs/${encodeURIComponent(id)}/artifacts/frames`),
  artifactSpec: (id: string) =>
    jfetch<SpecBundleView>(`/v1/jobs/${encodeURIComponent(id)}/artifacts/spec`),
  artifactVideo: (id: string) =>
    jfetch<VideoArtifactView>(`/v1/jobs/${encodeURIComponent(id)}/artifacts/video`),

  // Probe the evidence bundle without downloading it. Lets the SPA pick
  // between three states up front: "available" (200), "unavailable"
  // (410 + code=artifact_unavailable, e.g. job from before the bundle
  // feature shipped), or "expired" (410 + code=artifact_expired). HEAD
  // works because axum dispatches it through the GET handler and just
  // drops the body.
  probeEvidence: async (
    id: string,
  ): Promise<
    | { status: "available" }
    | { status: "unavailable" }
    | { status: "expired"; expiredAt: string | null }
    | { status: "error"; message: string }
  > => {
    try {
      const resp = await fetch(
        `/v1/jobs/${encodeURIComponent(id)}/evidence/download`,
        { credentials: "include", method: "HEAD" },
      );
      if (resp.ok) return { status: "available" };
      if (resp.status === 410) {
        // HEAD strips the body; re-fetch with GET so we can read the
        // problem JSON. Cheap — GCS will 404 fast for a missing object.
        const get = await fetch(
          `/v1/jobs/${encodeURIComponent(id)}/evidence/download`,
          { credentials: "include" },
        );
        let body: ProblemBody | null = null;
        try {
          body = (await get.json()) as ProblemBody;
        } catch {
          // not JSON
        }
        const code = body ? problemCode(body) : null;
        if (code === "artifact_expired") {
          return {
            status: "expired",
            expiredAt:
              typeof body?.expired_at === "string" ? body.expired_at : null,
          };
        }
        return { status: "unavailable" };
      }
      return { status: "error", message: `${resp.status} ${resp.statusText}` };
    } catch (e) {
      return {
        status: "error",
        message: e instanceof Error ? e.message : String(e),
      };
    }
  },

  // --- User-issued API keys ---

  apiKeys: {
    list: () => jfetch<ListApiKeysResponse>("/v1/keys"),
    create: (req: { name: string }) =>
      jfetch<CreateApiKeyResponse>("/v1/keys", {
        method: "POST",
        body: JSON.stringify(req),
      }),
    revoke: (keyId: string) =>
      jfetch<void>(`/v1/keys/${encodeURIComponent(keyId)}`, {
        method: "DELETE",
      }),
  },

  // --- Organizations (tenancy axis) ---

  orgs: {
    list: () => jfetch<OrgsListResponse>("/v1/orgs"),
    get: (orgId: string) =>
      jfetch<OrgWithRole>(`/v1/orgs/${encodeURIComponent(orgId)}`),
    create: (req: CreateOrgRequest) =>
      jfetch<OrgWithRole>("/v1/orgs", {
        method: "POST",
        body: JSON.stringify(req),
      }),
    update: (orgId: string, patch: UpdateOrgRequest) =>
      jfetch<OrgPublic>(`/v1/orgs/${encodeURIComponent(orgId)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    activate: (orgId: string) =>
      jfetch<void>(`/v1/orgs/${encodeURIComponent(orgId)}/activate`, {
        method: "POST",
      }),
    members: {
      list: (orgId: string) =>
        jfetch<MembersListResponse>(
          `/v1/orgs/${encodeURIComponent(orgId)}/members`,
        ),
      add: (orgId: string, req: AddMemberRequest) =>
        jfetch<void>(`/v1/orgs/${encodeURIComponent(orgId)}/members`, {
          method: "POST",
          body: JSON.stringify(req),
        }),
      remove: (orgId: string, userId: string) =>
        jfetch<void>(
          `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
          { method: "DELETE" },
        ),
      updateRole: (orgId: string, userId: string, role: OrgRole) =>
        jfetch<void>(
          `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
          { method: "PATCH", body: JSON.stringify({ role }) },
        ),
    },
  },

  // The flow endpoint returns text/plain (raw Mermaid), not JSON.
  artifactFlow: async (id: string): Promise<string> => {
    const resp = await fetch(
      `/v1/jobs/${encodeURIComponent(id)}/artifacts/flow`,
      { credentials: "include" },
    );
    if (!resp.ok) {
      let body: ProblemBody | null = null;
      try {
        body = (await resp.json()) as ProblemBody;
      } catch {
        // not JSON; leave body null
      }
      const detail =
        body?.detail || body?.title || `${resp.status} ${resp.statusText}`;
      if (resp.status === 410 && body) {
        const code = problemCode(body);
        if (code === "artifact_expired" || code === "artifact_unavailable") {
          throw new ArtifactExpiredError(
            code,
            detail,
            typeof body.expired_at === "string" ? body.expired_at : null,
          );
        }
      }
      throw new ApiError(resp.status, detail);
    }
    return resp.text();
  },
};

/// PUT a file directly to a signed GCS URL with progress callbacks.
/// Uses XHR (not fetch) because fetch doesn't expose upload progress events
/// across all browsers yet.
export function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`PUT failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error("network error during upload"));
    xhr.onabort = () => reject(new Error("upload aborted"));
    xhr.send(file);
  });
}

export function newIdempotencyKey(): string {
  // 16 random hex chars — fine for client-side dedupe, the api only uses
  // it within the user's namespace.
  const a = new Uint8Array(8);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}
